import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { ENV } from '@/lib/env';
function GateSkeleton() {
    return (_jsxs("div", { className: "flex min-h-screen flex-col items-center justify-center bg-cream-50", children: [_jsx("div", { className: "mb-6 h-16 w-16 animate-pulse rounded-full bg-olive-200" }), _jsx("div", { className: "mb-3 h-4 w-40 animate-pulse rounded bg-cream-200" }), _jsx("div", { className: "h-4 w-28 animate-pulse rounded bg-cream-200" }), _jsx("p", { className: "mt-8 text-sm text-olive-600", children: "Chargement\u2026" })] }));
}
/**
 * Inner gate implementation - always renders hooks (hooks-rules safe).
 * See DevOnlyGate below for the bypass wrapper.
 */
function GateInner({ children }) {
    const [status, setStatus] = useState('loading');
    useEffect(() => {
        const url = `${ENV.apiBaseUrl}${ENV.apiPrefix}/auth/me`;
        // Storefront and dashboard share origin (single Render service), so they
        // share localStorage. Reuse the dashboard's JWT (key 'lpc.token') as
        // Bearer auth - works without third-party cookies (which Chrome/Safari
        // block by default for cross-origin API calls). credentials: 'include'
        // is kept as a fallback for the cookie path.
        const token = typeof window !== 'undefined' ? window.localStorage.getItem('lpc.token') : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        fetch(url, { credentials: 'include', headers })
            .then(async (res) => {
            if (!res.ok) {
                setStatus('rejected');
                return;
            }
            const data = (await res.json());
            // Allow developer + admin roles through the pre-launch gate.
            if (data.role === 'developer' || data.role === 'admin') {
                setStatus('allowed');
            }
            else {
                setStatus('rejected');
            }
        })
            .catch(() => setStatus('rejected'));
    }, []);
    useEffect(() => {
        if (status === 'rejected') {
            // Dashboard lives at /admin/ in prod (same origin) and at :5173/ in dev.
            // ENV.dashboardUrl is '' in prod → relative path; full URL in dev.
            window.location.assign(`${ENV.dashboardUrl}/admin/sign-in`);
        }
    }, [status]);
    if (status === 'loading')
        return _jsx(GateSkeleton, {});
    if (status === 'rejected')
        return _jsx(_Fragment, {});
    return _jsx(_Fragment, { children: children });
}
/**
 * Phase 1 developer-only gate.
 * Calls GET /api/v1/auth/me with credentials: 'include'.
 * Non-developer roles are redirected to dashboard /sign-in.
 * Removed at public launch (see docs/plans/customer-storefront/03-routing-and-auth.md §6).
 *
 * Dev bypass: in development mode, append `?__bypass_gate=1` to the URL to skip
 * the /auth/me check and render children directly. This is intentional - the URL
 * param keeps it explicit and prevents accidental bypassing in production
 * (import.meta.env.DEV is false in production builds, making this dead code).
 * Example: http://localhost:5174/?__bypass_gate=1
 */
export function DevOnlyGate({ children }) {
    // import.meta.env.DEV is statically false in production builds → tree-shaken out.
    if (import.meta.env.DEV && window.location.search.includes('__bypass_gate=1')) {
        return _jsx(_Fragment, { children: children });
    }
    return _jsx(GateInner, { children: children });
}
